import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { RescomApiService } from '@app/res-common/services/rescom-api.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, filter, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse, IDynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { ResGuestPreference, IReservationApiBookingRequestParameters, ResBooking } from '../models/res-booking.model';
import { IReservationApiGetCounty, IReservationApiGetState } from '../models/res-country-state-county.models';
import { IReservationAddServiceOptionParams } from '../models/res-option.models';
import { ResPackage } from '../models/res-package.model';
import { IResPackageSearchParams } from '../models/res-search.models';

@Injectable({
  providedIn: 'root'
})
export class ReservationApiCoreService extends EidosBaseApiService {
  private get rescomApiService(): RescomApiService {
    return this.injector.get(RescomApiService);
  }

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService,
    private injector: Injector) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  public addServiceOptions(params: IReservationAddServiceOptionParams) {
    return this.postDynamicAPI('serviceOption/add', params);
  }

  /**
   * Retrieves booking matching with filters specified
   *
   * @param {IReservationApiBookingRequestParameters} [filter]
   * @param {boolean} [lightMode=false] Specify to retrieve booking in a minimal structure
   * @return {*}  {Observable<ResBooking>}
   * @memberof ReservationApiCoreService
   */
  getBooking(
    filter?: IReservationApiBookingRequestParameters,
    lightMode: boolean = false
  ): Observable<ResBooking> {
    let url = lightMode ? 'lightBooking/select' : 'booking/selectOptimized';

    return this.callDynamicAPI(url, filter, 'GET').pipe(
      map<DynamicApiResponse, ResBooking>((response) => {
        const data = lightMode ? response.body : this.normalizeBookingGet(response.body)
        return new ResBooking(data)
      }),
      catchError((response: IDynamicApiResponse) => {
        return of(ResBooking.ErrorResponse(filter?.BkgID ?? 0, response.Errors ?? [], response));
      })
    );
  }

  public getCounties(params: IReservationApiGetCounty): Observable<Array<any>> {
    return this.callDynamicAPI('county/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public getCountries(search?: string, cod?: number): Observable<Array<any>> {
    let params: any = {};
    if (!!search) {
      params.Country = search;
    }
    if (!!cod) {
      params.CountryCod = cod;
    }

    return this.callDynamicAPI('country/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  getExtraSegments() {
    return this.callDynamicAPI('lookup/getExtraSegments').pipe(
      map<DynamicApiResponse, any>((response) => {
        return response.body;
      })
    )
  }

  public getGuestPreferences(bkgID: number, Id: number): Observable<ResGuestPreference[]> {
    const params = { Id, BkgID: bkgID, Take: '1000', EntityType: 'customer' };
    return this.postDynamicAPI('customer/getpreferences', params).pipe(
      map<DynamicApiResponse, ResGuestPreference[]>((response) => {
        return (response.body ?? []).map((data: any) => new ResGuestPreference(data));
      })
    );
  }

  public getJwtToken() {
    return this.rescomApiService.getJwtToken();
  }

  public getPackageList(params: IResPackageSearchParams): Observable<ResPackage[]> {

    return this.callDynamicAPI(
      'product/searchLightPKG',
      params,
      'POST'
    ).pipe(
      map<DynamicApiResponse, ResPackage[]>(
        (response) => response.body = response.body.map((pkg: any) => new ResPackage(pkg))
      )
    );
  }

  public getStates(params: IReservationApiGetState): Observable<Array<any>> {
    return this.callDynamicAPI('state/select', params).pipe(
      map<DynamicApiResponse, Array<any>>((response) => {
        return response.body;
      })
    );
  }

  public normalizeBookingGet(data: any) {

    const bkg = Object.assign({}, data?.DT0[0] ?? {})
    bkg.Owner = data?.DT1[0] ?? {}
    bkg.FinacialRecap = data.DT2 ?? []
    bkg.Voyages = data?.DT3 ?? []
    bkg.Voyages.forEach((v: any) => {
      v.Itineraries = data?.DT9.filter((i: any) => i.VoyageID == v.VoyageID)
      v.Features = data?.DT10.filter((f: any) => f.VoyageID == v.VoyageID)
    })

    bkg.Supplements = data.DT4
    bkg.Supplements.forEach((s: any) => {
      s.Promo = data?.DT8.find((p: any) => p.PromoID == s.PromoID) ?? {}
    })

    bkg.Pax = data?.DT5
    bkg.Pax.forEach((s: any) => {
      s.GuestOwnArrangments = data?.DT16.filter((d: any) => d.DetailID == s.DetailID)
    })

    bkg.Deposit = data?.DT6
    bkg.Penalties = data?.DT7

    bkg.CustomPackages = data?.DT11
    bkg.CustomPackages.forEach((s: any) => {
      s.CustomPackagesDetail = data?.DT12.filter((d: any) => d.DetailID == s.DetailID)
    })

    bkg.Packages = data?.DT13
    bkg.PriceJourney = data?.DT14
    bkg.JourneySupplements = data?.DT15

    bkg.Links = data?.DT17
    bkg.EmbarkDebarkInfo = data?.DT18[0] ?? {}
    bkg.EmbarkDebarkDetails = data?.DT19
    bkg.RoomsConfig = data?.DT20
    bkg.SummaryNotes = data?.DT21

    return bkg
  }
}
